import {
	DefaultCellRenderer,
	DefaultColumnRenderer,
} from '@dealstack/frontend/shared/components/Table/Table/Table';
import {
	ColumnData,
	ColumnDefinition,
	CellRendererComponent,
	ColumnRendererComponent,
	DataToRender,
	TData,
	SortConfig,
} from '@dealstack/frontend/shared/components/Table/Table/types';
import { throttle } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const DEFAULT_COLUMN_WIDTH = 160;
const DEFAULT_OVERSCAN = 10;

export const CHECKBOX_COLUMN_WIDTH = 90;

export interface UseTableOptions<T extends TData> {
	columns: ColumnDefinition[];
	data: T[];
	totalCount: number;
	tableHeight: number;
	rowSize: number;
	defaultColumnWidth?: number;
	overscan?: number;
	CustomColumnRenderer?: ColumnRendererComponent;
	CustomCellRenderer?: CellRendererComponent<T>;
	customCellValueFn?: (column: ColumnData, item: T) => unknown;
	onDataRangeChange?: (start: number, end: number) => void;
	onSortingChange?: (sortConfig?: SortConfig) => void;
	onSelectChange?: (selectedIds: Set<string>) => void;
	rowSelect?: boolean;
}

export type ColumnsPerLevel = Record<string, ColumnData[][]>;

export interface UseTable<T extends TData> {
	setScrollContainer: (scrollContainer: HTMLDivElement) => void;
	tableHeight: number;
	virtualHeight: number;
	rowSize: number;
	columnsWidth: number;
	hasPinnedColumns: boolean;
	pinnedColumnsWidth: number;
	pinnedColumns: ColumnData[];
	nonPinnedColumns: ColumnData[];
	subcolumnsLevels: number;
	columnsPerLevel: ColumnsPerLevel;
	deepestPinnedColumns: ColumnData[];
	deepestNonPinnedColumns: ColumnData[];
	dataToRender: DataToRender<T>[];
	ColumnRenderer: ColumnRendererComponent;
	CellRenderer: CellRendererComponent<T>;
	cellValueFn: (column: ColumnData, item: T) => unknown;
	rowSelect: boolean;
	onSelectedRowToggle: (id: string) => void;
	isRowSelected: (id: string) => boolean;
	selectedAll: boolean;
	onSelectAllButton: (select: boolean) => void;
	sortConfig?: SortConfig;
	onSortingClick: (key: string) => void;
}

export function calculateColumnsLayout(columns: ColumnData[]): void {
	function iterateOverColumns(
		columns: ColumnData[],
		callback: (
			column: ColumnData,
			siblingColumns: ColumnData[],
			parentColumn?: ColumnData
		) => void,
		parentColumn?: ColumnData
	) {
		for (const column of columns) {
			if (column.subcolumns?.length > 0) {
				iterateOverColumns(column.subcolumns, callback, column);
			}

			callback(column, columns, parentColumn);
		}
	}

	iterateOverColumns(columns, (column, siblingColumns, parentColumn) => {
		const isRoot = !parentColumn;

		let requestedWidth = 0;

		if (column.width) {
			requestedWidth = column.width;
		} else {
			const childrenTotalWidth = column.subcolumns.reduce(
				(acc, column) => acc + (column.width || 0),
				0
			);

			if (isRoot) {
				requestedWidth = childrenTotalWidth || DEFAULT_COLUMN_WIDTH;
			} else if (parentColumn.width) {
				const columnsLeftToDistribute = siblingColumns.filter(
					(column) => !column.width
				).length;

				const siblingColumnsTotalWidth = siblingColumns.reduce(
					(acc, column) => acc + (column.width || 0),
					0
				);

				if (siblingColumnsTotalWidth) {
					requestedWidth =
						(parentColumn.width - siblingColumnsTotalWidth) /
						(columnsLeftToDistribute || 1);
				} else {
					requestedWidth = parentColumn.width / (columnsLeftToDistribute || 1);
				}
			} else {
				requestedWidth = childrenTotalWidth || DEFAULT_COLUMN_WIDTH;
			}
		}

		column.width = requestedWidth;
	});

	// Calculate offsets
	// d.skuza: probably possible to do in a single iteration above but I'm too lazy to figure it out
	calculateColumnsOffsets(columns);
}

function calculateColumnsOffsets(columns: ColumnData[]): void {
	for (const column of columns) {
		const columnsPerLevel: ColumnData[][] = [];

		const findColumnsOnLevel = (column: ColumnData, level: number) => {
			columnsPerLevel[level] = columnsPerLevel[level] || [];

			if (level > 0 && columnsPerLevel[level].length === 0) {
				// nothing was there before, we are the first column on this level
				// we can use this info to calculate padding required to align this column

				const parentLevel = level - 1;
				const parentColumn = columnsPerLevel[parentLevel].find((c) =>
					c.subcolumns?.includes(column)
				);

				let offset = 0;

				for (const siblingColumn of columnsPerLevel[parentLevel]) {
					if (siblingColumn === parentColumn) {
						break;
					}

					offset += siblingColumn.width;
				}

				column.offset = offset;
			}

			columnsPerLevel[level].push(column);

			if (column.subcolumns) {
				column.subcolumns.forEach((subcolumn) => {
					findColumnsOnLevel(subcolumn, level + 1);
				});
			}
		};

		findColumnsOnLevel(column, 0);
	}
}

export function mapColumns(columns: ColumnDefinition[]): ColumnData[] {
	return columns.map((column, index): ColumnData => {
		return {
			index,
			id: column.id,
			label: column.label,
			pinned: !!column.pinned,
			sortable: !!column.sortable,
			subcolumns: column.subcolumns ? mapColumns(column.subcolumns) : [],
			width: column.width || 0,
			offset: 0,
			dataTestId: column.dataTestId,
		};
	});
}

export function defaultCellValueFn<T extends TData>(column: ColumnData, item: T): unknown {
	return item[column.id];
}

export function useTable<T extends TData>(options: UseTableOptions<T>): UseTable<T> {
	const overscan = options.overscan ?? DEFAULT_OVERSCAN;

	const [scrollContainer, setScrollContainer] = useState<HTMLDivElement | null>(null);
	const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

	const clearSelected = useCallback(() => {
		const newState = new Set<string>();

		setSelectedIds(newState);

		options.onSelectChange && options.onSelectChange(newState);
	}, [options.onSelectChange]);

	useEffect(() => {
		if (!options.rowSelect) {
			clearSelected();
		}
	}, [options.rowSelect]);

	const onSelectedRowToggle = useCallback((id: string) => {
		setSelectedIds((prevState) => {
			if (prevState.has(id)) {
				prevState.delete(id);
			} else {
				prevState.add(id);
			}

			const newState = new Set(prevState);

			options.onSelectChange && options.onSelectChange(newState);

			return newState;
		});
	}, []);

	const onSelectAllButton = useCallback((select: boolean) => {
		if (select) {
			const newState = new Set(options.data.map((row) => row.id));

			setSelectedIds(newState);

			options.onSelectChange && options.onSelectChange(newState);
		} else {
			clearSelected();
		}
	}, []);

	const isRowSelected = selectedIds.has.bind(selectedIds);
	const selectedAll = selectedIds.size === options.totalCount;

	const optionsColumns = options.columns;

	const subcolumnsLevels = useMemo(() => {
		const calculateSubcolumnsLevels = (columns: ColumnDefinition[]): number => {
			return columns.reduce(
				(acc, column) =>
					Math.max(
						acc,
						column.subcolumns ? calculateSubcolumnsLevels(column.subcolumns) + 1 : 1
					),
				0
			);
		};

		return calculateSubcolumnsLevels(optionsColumns);
	}, [optionsColumns]);

	const pinnedColumns = useMemo(() => {
		const mappedColumns = mapColumns(optionsColumns.filter((column) => column.pinned));

		calculateColumnsLayout(mappedColumns);

		return mappedColumns;
	}, [optionsColumns, subcolumnsLevels]);

	const nonPinnedColumns = useMemo(() => {
		const mappedColumns = mapColumns(optionsColumns.filter((column) => !column.pinned));

		calculateColumnsLayout(mappedColumns);

		return mappedColumns;
	}, [optionsColumns, subcolumnsLevels]);

	const columns = useMemo(() => {
		return [...pinnedColumns, ...nonPinnedColumns];
	}, [pinnedColumns, nonPinnedColumns]);

	const columnsWidth = useMemo(() => {
		return columns.reduce((acc, column) => acc + column.width, 0);
	}, [columns]);

	const pinnedColumnsWidth = useMemo(() => {
		return pinnedColumns.reduce((acc, column) => acc + column.width, 0);
	}, [pinnedColumns]);

	const columnsPerLevel: ColumnsPerLevel = useMemo(() => {
		const allColumnsPerLevel: ColumnsPerLevel = {};

		for (const column of columns) {
			const columnsPerLevel: ColumnData[][] = [];

			const findColumnsOnLevel = (column: ColumnData, level: number) => {
				columnsPerLevel[level] = columnsPerLevel[level] || [];

				columnsPerLevel[level].push(column);

				if (column.subcolumns) {
					column.subcolumns.forEach((subcolumn) => {
						findColumnsOnLevel(subcolumn, level + 1);
					});
				}
			};

			findColumnsOnLevel(column, 0);

			if (columnsPerLevel.length < subcolumnsLevels) {
				for (let i = columnsPerLevel.length; i < subcolumnsLevels; i++) {
					columnsPerLevel.unshift([]);
				}
			}

			allColumnsPerLevel[column.id] = columnsPerLevel;
		}

		return allColumnsPerLevel;
	}, [columns, subcolumnsLevels]);

	const deepestPinnedColumns: ColumnData[] = useMemo(() => {
		return pinnedColumns.flatMap((column) => {
			return columnsPerLevel[column.id][subcolumnsLevels - 1];
		});
	}, [pinnedColumns, columnsPerLevel, subcolumnsLevels]);

	const deepestNonPinnedColumns: ColumnData[] = useMemo(() => {
		return nonPinnedColumns.flatMap((column) => {
			return columnsPerLevel[column.id][subcolumnsLevels - 1];
		});
	}, [nonPinnedColumns, columnsPerLevel, subcolumnsLevels]);

	const [dataToRender, setDataToRender] = useState<DataToRender<T>[]>([]);

	const dataRange = useRef({ rowStart: 0, rowEnd: 0 });

	useEffect(() => {
		if (!scrollContainer) {
			return;
		}

		const createGridRow = (start: number, end: number) => `${start} / ${end}`;

		const recalculateVisibleRanges = throttle(() => {
			let rowIndex = Math.floor(scrollContainer.scrollTop / options.rowSize);
			let endRowIndex = Math.floor(
				(scrollContainer.scrollTop + scrollContainer.clientHeight) / options.rowSize
			);

			// add overscan
			rowIndex = Math.max(0, rowIndex - overscan);
			endRowIndex = Math.min(options.totalCount, endRowIndex + overscan);

			// round indexes to 5 to avoid unnecessary rerenders
			rowIndex = Math.floor(rowIndex / 5) * 5;
			endRowIndex = Math.ceil(endRowIndex / 5) * 5;

			if (
				rowIndex !== dataRange.current.rowStart ||
				endRowIndex !== dataRange.current.rowEnd
			) {
				options.onDataRangeChange?.(rowIndex, endRowIndex);

				const items: DataToRender<T>[] = [];

				for (let i = rowIndex; i < endRowIndex; i++) {
					const item = options.data[i];

					if (item) {
						items.push({
							...item,
							gridRow: createGridRow(i + 1, i + 2),
						});
					}
				}

				setDataToRender(items);
			}

			dataRange.current.rowStart = rowIndex;
			dataRange.current.rowEnd = endRowIndex;
		}, 33);

		recalculateVisibleRanges();

		scrollContainer.addEventListener('scroll', recalculateVisibleRanges);

		return () => {
			scrollContainer.removeEventListener('scroll', recalculateVisibleRanges);
		};
	}, [
		scrollContainer,
		options.data,
		options.totalCount,
		options.rowSize,
		options.onDataRangeChange,
		overscan,
	]);

	const [sortConfig, setSortConfig] = useState<SortConfig>();

	const onSortingClick = (key: string) => {
		if (sortConfig && sortConfig.key === key && sortConfig.direction === 'desc') {
			setSortConfig(undefined);

			options.onSortingChange && options.onSortingChange();

			return;
		}

		let direction: SortConfig['direction'] = 'asc';

		if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
			direction = 'desc';
		}

		setSortConfig({ key, direction });

		options.onSortingChange && options.onSortingChange({ key, direction });
	};

	return {
		setScrollContainer,
		tableHeight: options.tableHeight,
		virtualHeight: options.totalCount * options.rowSize,
		columnsWidth,
		pinnedColumns,
		nonPinnedColumns,
		pinnedColumnsWidth,
		hasPinnedColumns: pinnedColumns.length > 0,
		rowSize: options.rowSize,
		subcolumnsLevels,
		columnsPerLevel,
		dataToRender,
		deepestPinnedColumns,
		deepestNonPinnedColumns,
		ColumnRenderer: options.CustomColumnRenderer ?? DefaultColumnRenderer,
		CellRenderer: options.CustomCellRenderer ?? DefaultCellRenderer,
		cellValueFn: options.customCellValueFn ?? defaultCellValueFn,
		rowSelect: options.rowSelect ?? false,
		onSelectedRowToggle,
		isRowSelected,
		selectedAll,
		onSelectAllButton,
		sortConfig,
		onSortingClick,
	};
}
